import React, { useEffect, useState } from 'react';
import { Loader2, Users, Building2, GitBranch, ChevronRight, ChevronDown, UserX, Sparkles, AlertCircle, CheckCircle2 } from 'lucide-react';
import { atlasApi, AtlasUser, HierarquiaNodo, HierarquiaResp, HierarquiaSugestao } from '../shared/atlasApi';
import AvatarColaborador from '../shared/AvatarColaborador';

interface OrgProps {
  user: AtlasUser;
  podeEditar?: boolean;
  onAbrir: (funccodi: number) => void;
}

interface ColabRef {
  funccodi: number;
  nome?: string;
  cargo_nome?: string;
  foto_url?: string;
  tem_foto_gix?: boolean;
  gestor_funccodi?: number | null;
}

interface ColabNodo extends ColabRef {
  subordinados: ColabNodo[];
}

function montarArvoreCC(lista: ColabRef[]): ColabNodo[] {
  const mapa = new Map<number, ColabNodo>();
  lista.forEach(c => mapa.set(c.funccodi, { ...c, subordinados: [] }));
  const raizes: ColabNodo[] = [];
  mapa.forEach(n => {
    const g = n.gestor_funccodi;
    if (g != null && mapa.has(g) && g !== n.funccodi) {
      mapa.get(g)!.subordinados.push(n);
    } else {
      raizes.push(n);
    }
  });
  const sortF = (a: ColabNodo, b: ColabNodo) => {
    const d = b.subordinados.length - a.subordinados.length;
    if (d !== 0) return d;
    return (a.nome || '').localeCompare(b.nome || '');
  };
  const ord = (l: ColabNodo[]) => { l.sort(sortF); l.forEach(c => ord(c.subordinados)); };
  ord(raizes);
  return raizes;
}

interface Filial {
  funcempe?: number;
  filial_nome: string;
  total: number;
  colaboradores: ColabRef[];
}

interface Grupo {
  centro_custo_codigo?: number | string;
  centro_custo_nome: string;
  total: number;
  colaboradores?: ColabRef[];
  filiais?: Filial[];
}

type Modo = 'cc' | 'hierarquia';

const Organograma: React.FC<OrgProps> = ({ user, podeEditar, onAbrir }) => {
  const [modo, setModo] = useState<Modo>('cc');
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());

  const [hier, setHier] = useState<HierarquiaResp | null>(null);
  const [loadingHier, setLoadingHier] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    atlasApi.organograma(user)
      .then(r => { if (!cancel) setGrupos(r.grupos || []); })
      .catch(e => { if (!cancel) console.error('[Atlas] organograma', e); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user]);

  useEffect(() => {
    if (modo !== 'hierarquia' || hier !== null) return;
    let cancel = false;
    setLoadingHier(true);
    atlasApi.hierarquia(user)
      .then(r => { if (!cancel) setHier(r); })
      .catch(e => { if (!cancel) console.error('[Atlas] hierarquia', e); })
      .finally(() => { if (!cancel) setLoadingHier(false); });
    return () => { cancel = true; };
  }, [user, modo, hier]);

  const toggle = (id: string) => {
    setExpandidos(s => {
      const nova = new Set(s);
      if (nova.has(id)) nova.delete(id); else nova.add(id);
      return nova;
    });
  };

  const renderColabs = (lista: ColabRef[]) => {
    const raizes = montarArvoreCC(lista);
    if (raizes.length === 0) {
      return <div className="text-xs text-gray-400 italic">Nenhum colaborador.</div>;
    }
    return (
      <ul className="space-y-1">
        {raizes.map(n => (
          <NodoCC key={n.funccodi} user={user} nodo={n} onAbrir={onAbrir} nivel={0} aberto={raizes.length === 1} />
        ))}
      </ul>
    );
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setModo('cc')}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
            modo === 'cc' ? 'bg-purple-100 text-purple-700' : 'bg-white border border-gray-200 text-gray-600 hover:border-purple-300'
          }`}
        >
          <Users className="w-4 h-4" /> Por Centro de Custo
        </button>
        <button
          onClick={() => setModo('hierarquia')}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
            modo === 'hierarquia' ? 'bg-purple-100 text-purple-700' : 'bg-white border border-gray-200 text-gray-600 hover:border-purple-300'
          }`}
        >
          <GitBranch className="w-4 h-4" /> Hierarquia
        </button>
      </div>

      {modo === 'cc' && (
        <CcView
          loading={loading}
          grupos={grupos}
          expandidos={expandidos}
          toggle={toggle}
          renderColabs={renderColabs}
        />
      )}

      {modo === 'hierarquia' && (
        <HierarquiaView
          user={user}
          podeEditar={!!podeEditar}
          loading={loadingHier}
          data={hier}
          onAbrir={onAbrir}
          onRecarregar={() => setHier(null)}
        />
      )}
    </div>
  );
};

interface CcViewProps {
  loading: boolean;
  grupos: Grupo[];
  expandidos: Set<string>;
  toggle: (id: string) => void;
  renderColabs: (lista: ColabRef[]) => React.ReactNode;
}

const CcView: React.FC<CcViewProps> = ({ loading, grupos, expandidos, toggle, renderColabs }) => {
  if (loading) {
    return (
      <div className="p-12 text-center text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Carregando organograma...
      </div>
    );
  }
  if (grupos.length === 0) {
    return (
      <div className="p-12 text-center text-gray-400">
        <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        Nenhum colaborador ativo encontrado.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {grupos.map(g => {
        const id = String(g.centro_custo_codigo || g.centro_custo_nome);
        const aberto = expandidos.has(id);
        const temSubfiliais = Array.isArray(g.filiais) && g.filiais.length > 0;
        return (
          <div key={id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggle(id)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-purple-50/40 transition"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5" />
                </div>
                <div className="min-w-0 text-left">
                  <div className="font-semibold text-gray-900 truncate">{g.centro_custo_nome}</div>
                  <div className="text-xs text-gray-400">
                    {g.total} colaborador{g.total === 1 ? '' : 'es'}
                    {temSubfiliais ? ` · ${g.filiais!.length} filiais` : ''}
                  </div>
                </div>
              </div>
              <span className="text-purple-600 text-sm font-medium">{aberto ? '−' : '+'}</span>
            </button>
            {aberto && temSubfiliais && (
              <div className="px-5 pb-5 space-y-2 border-t border-gray-100 pt-3">
                {g.filiais!.map(f => {
                  const subId = `${id}::${f.funcempe ?? 'na'}`;
                  const subAberto = expandidos.has(subId);
                  return (
                    <div key={subId} className="border border-gray-100 rounded-xl overflow-hidden">
                      <button
                        onClick={() => toggle(subId)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-purple-50/30 transition"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 text-left">
                            <div className="text-sm font-medium text-gray-800 truncate">{f.filial_nome}</div>
                            <div className="text-[11px] text-gray-400">{f.total} colaborador{f.total === 1 ? '' : 'es'}</div>
                          </div>
                        </div>
                        <span className="text-indigo-500 text-sm font-medium">{subAberto ? '−' : '+'}</span>
                      </button>
                      {subAberto && (
                        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                          {renderColabs(f.colaboradores)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {aberto && !temSubfiliais && g.colaboradores && (
              <div className="px-5 pb-5 border-t border-gray-100 pt-3">
                {renderColabs(g.colaboradores)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface HierarquiaViewProps {
  user: AtlasUser;
  podeEditar: boolean;
  loading: boolean;
  data: HierarquiaResp | null;
  onAbrir: (funccodi: number) => void;
  onRecarregar: () => void;
}

const HierarquiaView: React.FC<HierarquiaViewProps> = ({ user, podeEditar, loading, data, onAbrir, onRecarregar }) => {
  const [sugestoes, setSugestoes] = useState<HierarquiaSugestao[] | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ total_colaboradores: number; sem_cargo_reconhecido: number } | null>(null);
  const [showSugest, setShowSugest] = useState(false);

  const calcular = async () => {
    setCalculando(true);
    setErro(null);
    setMsg(null);
    try {
      const r = await atlasApi.hierarquiaAutoSugerir(user, { aplicar: false });
      setSugestoes(r.sugestoes);
      setMeta({ total_colaboradores: r.total_colaboradores, sem_cargo_reconhecido: r.sem_cargo_reconhecido });
      setShowSugest(true);
      if (r.sugestoes.length === 0) {
        setMsg('Nenhuma sugestão nova. Todos os colaboradores já têm gestor ou não foi possível inferir.');
      }
    } catch (e: any) {
      setErro(e?.message || 'Erro ao calcular sugestões.');
    } finally {
      setCalculando(false);
    }
  };

  const aplicar = async () => {
    if (!sugestoes || sugestoes.length === 0) return;
    if (!confirm(`Aplicar ${sugestoes.length} sugestão(ões) de gestor? Apenas quem ainda não tem gestor será afetado.`)) return;
    setAplicando(true);
    setErro(null);
    try {
      const r = await atlasApi.hierarquiaAutoSugerir(user, { aplicar: true });
      setMsg(`${r.aplicados} gestor(es) aplicado(s) com sucesso.`);
      setSugestoes(null);
      setShowSugest(false);
      onRecarregar();
    } catch (e: any) {
      setErro(e?.message || 'Erro ao aplicar sugestões.');
    } finally {
      setAplicando(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="p-12 text-center text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Carregando hierarquia...
      </div>
    );
  }

  const vazio = data.raizes.length === 0 && data.sem_hierarquia.length === 0;

  return (
    <div className="space-y-4">
      {podeEditar && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white text-purple-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900">Sugerir hierarquia automática</div>
              <p className="text-xs text-gray-600 mt-0.5">
                Infere o gestor de cada colaborador pelo nível do cargo (Diretor &gt; Gerente &gt; Coordenador &gt; Analista &gt; Assistente…) dentro do mesmo centro de custo. Quem já tem gestor não é alterado.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={calcular}
                  disabled={calculando || aplicando}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-purple-300 hover:border-purple-400 text-purple-700 text-xs font-semibold transition disabled:opacity-50"
                >
                  {calculando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Calcular sugestões
                </button>
                {sugestoes && sugestoes.length > 0 && (
                  <button
                    onClick={aplicar}
                    disabled={aplicando}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold transition disabled:opacity-50"
                  >
                    {aplicando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Aplicar {sugestoes.length} sugestão(ões)
                  </button>
                )}
              </div>
              {meta && (
                <p className="text-[11px] text-gray-500 mt-2">
                  {meta.total_colaboradores} colaborador(es) avaliado(s) · {meta.sem_cargo_reconhecido} com cargo não reconhecido pela heurística.
                </p>
              )}
              {erro && (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                  <AlertCircle className="w-3.5 h-3.5" /> {erro}
                </div>
              )}
              {msg && (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {msg}
                </div>
              )}
            </div>
          </div>

          {showSugest && sugestoes && sugestoes.length > 0 && (
            <div className="mt-3 bg-white rounded-xl border border-purple-100 overflow-hidden">
              <div className="px-3 py-2 bg-purple-50/50 text-[11px] font-semibold text-purple-700 uppercase tracking-wider flex items-center justify-between">
                <span>Pré-visualização ({sugestoes.length})</span>
                <button onClick={() => setShowSugest(false)} className="text-purple-600 hover:underline normal-case font-medium">
                  Ocultar
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                {sugestoes.map(s => (
                  <div key={s.funccodi} className="px-3 py-2 text-xs flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 truncate">{s.nome}</div>
                      <div className="text-gray-400 truncate">{s.cargo_nome || 'sem cargo'} · {s.centro_custo_nome || 'sem CC'}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-purple-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-purple-700 truncate">{s.gestor_nome}</div>
                      <div className="text-gray-400 truncate">{s.gestor_cargo}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {vazio ? (
        <div className="p-12 text-center text-gray-400">
          <GitBranch className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          Nenhuma hierarquia definida.
          <p className="text-xs mt-2">{podeEditar ? 'Use o botão acima ou defina gestores nas fichas dos colaboradores.' : 'Peça a um administrador para configurar a hierarquia.'}</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-3">
              Estrutura ({data.raizes.length} raíz{data.raizes.length === 1 ? '' : 'es'} · {data.total} no total)
            </div>
            {data.raizes.length === 0 ? (
              <p className="text-sm text-gray-400">Nenhum gestor raíz identificado.</p>
            ) : (
              <ul className="space-y-1">
                {data.raizes.map(n => (
                  <NodoArvore key={n.funccodi} user={user} nodo={n} onAbrir={onAbrir} nivel={0} aberto />
                ))}
              </ul>
            )}
          </div>

          {data.sem_hierarquia.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                <UserX className="w-4 h-4" />
                Sem hierarquia definida ({data.sem_hierarquia.length})
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {data.sem_hierarquia.map(c => {
                  const fotoUrl = c.tem_foto ? atlasApi.fotoUrl(user, c.funccodi) : undefined;
                  return (
                    <button
                      key={c.funccodi}
                      onClick={() => onAbrir(c.funccodi)}
                      className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 hover:border-purple-300 hover:bg-purple-50/30 transition text-left"
                    >
                      <AvatarColaborador nome={c.nome} fotoUrl={fotoUrl} size={36} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{c.nome || '—'}</div>
                        <div className="text-xs text-gray-400 truncate">{c.cargo_nome || 'sem cargo'}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// (sem helpers globais)

interface NodoArvoreProps {
  user: AtlasUser;
  nodo: HierarquiaNodo;
  onAbrir: (funccodi: number) => void;
  nivel: number;
  aberto?: boolean;
}

const NodoArvore: React.FC<NodoArvoreProps> = ({ user, nodo, onAbrir, nivel, aberto: abertoInicial }) => {
  const [aberto, setAberto] = useState<boolean>(!!abertoInicial);
  const tem = nodo.subordinados && nodo.subordinados.length > 0;
  const fotoUrl = nodo.tem_foto ? atlasApi.fotoUrl(user, nodo.funccodi) : undefined;
  return (
    <li>
      <div
        className="flex items-center gap-2 py-1.5 rounded-lg hover:bg-purple-50/40 transition"
        style={{ paddingLeft: nivel * 18 }}
      >
        <button
          onClick={() => tem && setAberto(a => !a)}
          className={`w-5 h-5 flex items-center justify-center rounded ${tem ? 'text-purple-600 hover:bg-purple-100' : 'text-transparent cursor-default'}`}
        >
          {tem ? (aberto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
        </button>
        <button
          onClick={() => onAbrir(nodo.funccodi)}
          className="flex items-center gap-2 min-w-0 text-left flex-1"
        >
          <AvatarColaborador nome={nodo.nome} fotoUrl={fotoUrl} size={28} />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-800 truncate">{nodo.nome || '—'}</div>
            <div className="text-[11px] text-gray-400 truncate">
              {nodo.cargo_nome || 'sem cargo'}
              {tem && <span className="ml-2 text-purple-600">· {nodo.subordinados.length} direto{nodo.subordinados.length === 1 ? '' : 's'}</span>}
            </div>
          </div>
        </button>
      </div>
      {tem && aberto && (
        <ul className="space-y-1">
          {nodo.subordinados.map(s => (
            <NodoArvore key={s.funccodi} user={user} nodo={s} onAbrir={onAbrir} nivel={nivel + 1} />
          ))}
        </ul>
      )}
    </li>
  );
};

export default Organograma;

interface NodoCCProps {
  user: AtlasUser;
  nodo: ColabNodo;
  onAbrir: (funccodi: number) => void;
  nivel: number;
  aberto?: boolean;
}

const NodoCC: React.FC<NodoCCProps> = ({ user, nodo, onAbrir, nivel, aberto: abertoInicial }) => {
  const [aberto, setAberto] = useState<boolean>(!!abertoInicial);
  const tem = nodo.subordinados && nodo.subordinados.length > 0;
  const fotoUrl = (nodo.tem_foto_gix || nodo.foto_url) ? atlasApi.fotoUrl(user, nodo.funccodi) : undefined;
  return (
    <li>
      <div
        className="flex items-center gap-2 py-1.5 rounded-lg hover:bg-purple-50/40 transition"
        style={{ paddingLeft: nivel * 18 }}
      >
        <button
          onClick={() => tem && setAberto(a => !a)}
          className={`w-5 h-5 flex items-center justify-center rounded ${tem ? 'text-purple-600 hover:bg-purple-100' : 'text-transparent cursor-default'}`}
        >
          {tem ? (aberto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
        </button>
        <button
          onClick={() => onAbrir(nodo.funccodi)}
          className="flex items-center gap-2 min-w-0 text-left flex-1"
        >
          <AvatarColaborador nome={nodo.nome} fotoUrl={fotoUrl} size={28} />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-800 truncate">{nodo.nome || '—'}</div>
            <div className="text-[11px] text-gray-400 truncate">
              {nodo.cargo_nome || 'sem cargo'}
              {tem && <span className="ml-2 text-purple-600">· {nodo.subordinados.length} direto{nodo.subordinados.length === 1 ? '' : 's'}</span>}
            </div>
          </div>
        </button>
      </div>
      {tem && aberto && (
        <ul className="space-y-1">
          {nodo.subordinados.map(s => (
            <NodoCC key={s.funccodi} user={user} nodo={s} onAbrir={onAbrir} nivel={nivel + 1} />
          ))}
        </ul>
      )}
    </li>
  );
};
